import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faClock, faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/http";
import styles from "../styles/Header.module.css";
import logo from "../images/logo.jpg";
import { isAdminFromAccess } from "../api/auth";

const Header = () => {
  const [user, setUser] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const [showCenter, setShowCenter] = useState(true);

  const menuRef = useRef(null);
  const headerInnerRef = useRef(null);
  const navRef = useRef(null);
  const rightRef = useRef(null);

  const navigate = useNavigate();

  const access = sessionStorage.getItem("access");
  const isAuthenticated = !!access;

  // ✅ ADMIN din JWT payload (is_superuser / is_staff)
  const isAdmin = isAdminFromAccess();

  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      return;
    }

    const loadUser = async () => {
      try {
        const res = await api.get("/users/profiles/me/");
        const data = res.data;
        setUser(Array.isArray(data) ? (data[0] ?? null) : data ?? null);
      } catch (err) {
        console.error("Failed to load user", err);
        setUser(null);
      }
    };

    loadUser();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w <= 860) {
        setShowCenter(false);
        return;
      }

      setShowCenter(true);
      setIsMobileNavOpen(false);

      const inner = headerInnerRef.current;
      const nav = navRef.current;
      const right = rightRef.current;

      if (!inner || !nav || !right) return;

      const innerW = inner.clientWidth;
      const navW = nav.scrollWidth;
      const rightW = right.clientWidth;
      const brandReserve = 86;
      const free = innerW - brandReserve - navW - rightW;

      setShowCenter(free > 260);
    };

    calc();
    window.addEventListener("resize", calc, { passive: true });
    return () => window.removeEventListener("resize", calc);
  }, []);

  const logout = () => {
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("refresh");
    setUser(null);
    setIsDropdownOpen(false);
    setIsMobileNavOpen(false);
    navigate("/login", { replace: true });
  };

  const closeMobileNav = () => setIsMobileNavOpen(false);

  return (
    <header
      className={[
        styles.srHeader,
        isScrolled ? styles.isScrolled : "",
        isMobileNavOpen ? styles.srHeader_menuOpen : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.srHeader__inner} ref={headerInnerRef}>
        {/* BRAND */}
        <Link to="/" className={styles.srHeader__brand} aria-label="Home" onClick={closeMobileNav}>
          <img src={logo} alt="Home" className={styles.srHeader__logo} />
        </Link>

        {/* NAV */}
        <nav className={styles.srHeader__nav} aria-label="Primary" ref={navRef}>
          <Link className={styles.srHeader__navLink} to="/about">
            DESPRE NOI
          </Link>
          <Link className={styles.srHeader__navLink} to="/conditii">
            TERMENI ȘI CONDIȚII
          </Link>

          {isAdmin && (
            <Link className={styles.srHeader__navLink} to="/admin">
              Admin
            </Link>
          )}
        </nav>

        {/* CENTER */}
        {showCenter && (
          <div className={styles.srHeader__center}>
            <div className={styles.srHeader__pill}>
              <span className={styles.srHeader__pillIcon} aria-hidden="true">
                🌍
              </span>
              <div className={styles.srHeader__pillText}>
                <div className={styles.srHeader__pillPrimary}>Bălți</div>
                <div className={styles.srHeader__pillSecondary}>Moldova</div>
              </div>
            </div>

            <div className={styles.srHeader__pill}>
              <FontAwesomeIcon className={styles.srHeader__pillIconFA} icon={faClock} />
              <div className={styles.srHeader__pillText}>
                <div className={styles.srHeader__pillPrimary}>Luni - Duminică</div>
                <div className={styles.srHeader__pillSecondary}>24/24</div>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT */}
        <div className={styles.srHeader__right} ref={rightRef}>
          <button
            type="button"
            className={styles.srHeader__hamburger}
            onClick={() => {
              setIsDropdownOpen(false);
              setIsMobileNavOpen(true);
            }}
            aria-label="Open menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className={styles.srHeader__user} ref={menuRef}>
            <button
              type="button"
              className={styles.srHeader__avatarBtn}
              onClick={() => setIsDropdownOpen((v) => !v)}
              aria-label="User menu"
              aria-expanded={isDropdownOpen}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt="Avatar" className={styles.srHeader__avatarImg} />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </button>

            {isDropdownOpen && (
              <div className={styles.srHeader__menu} role="menu">
                {isAuthenticated ? (
                  <>
                    {user && (
                      <div className={styles.srHeader__menuUser}>
                        <div className={styles.srHeader__menuUserName}>
                          {user.first_name} {user.last_name}
                        </div>
                        <div className={styles.srHeader__menuUserHint}>Contul tău</div>
                      </div>
                    )}

                    <Link
                      className={styles.srHeader__menuLink}
                      to="/profile"
                      onClick={() => setIsDropdownOpen(false)}
                      role="menuitem"
                    >
                      Profile
                    </Link>

                    <button
                      type="button"
                      onClick={logout}
                      className={styles.srHeader__menuDanger}
                      role="menuitem"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      className={styles.srHeader__menuLink}
                      to="/login"
                      onClick={() => setIsDropdownOpen(false)}
                      role="menuitem"
                    >
                      Login
                    </Link>
                    <Link
                      className={styles.srHeader__menuLink}
                      to="/register"
                      onClick={() => setIsDropdownOpen(false)}
                      role="menuitem"
                    >
                      Register
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isMobileNavOpen &&
        createPortal(
          <div className={styles.srHeader__drawerWrap} role="dialog" aria-modal="true">
            <button
              type="button"
              className={styles.srHeader__drawerBackdrop}
              aria-label="Închide meniul"
              onClick={closeMobileNav}
            />
            <aside className={styles.srHeader__drawer}>
              <div className={styles.srHeader__drawerContent}>
                <div className={styles.srHeader__drawerTop}>
                  <div className={styles.srHeader__drawerTitle}>Meniu</div>
                  <button
                    type="button"
                    className={styles.srHeader__drawerClose}
                    onClick={closeMobileNav}
                    aria-label="Close"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>

                <div className={styles.srHeader__drawerLinks}>
                  <Link className={styles.srHeader__drawerLink} to="/about" onClick={closeMobileNav}>
                    DESPRE NOI
                  </Link>
                  <Link
                    className={styles.srHeader__drawerLink}
                    to="/conditii"
                    onClick={closeMobileNav}
                  >
                    TERMENI ȘI CONDIȚII
                  </Link>
                  {isAdmin && (
                    <Link className={styles.srHeader__drawerLink} to="/admin" onClick={closeMobileNav}>
                      Admin
                    </Link>
                  )}
                </div>

                <div className={styles.srHeader__drawerProfile}>
                  {isAuthenticated ? (
                    <>
                      {user && (
                        <div className={styles.srHeader__drawerUser}>
                          <div className={styles.srHeader__drawerUserName}>
                            {user.first_name} {user.last_name}
                          </div>
                          <div className={styles.srHeader__drawerUserHint}>Contul tău</div>
                        </div>
                      )}
                      <Link
                        className={styles.srHeader__drawerLink}
                        to="/profile"
                        onClick={closeMobileNav}
                      >
                        Profile
                      </Link>
                      <button
                        type="button"
                        className={styles.srHeader__drawerLogout}
                        onClick={() => {
                          logout();
                          closeMobileNav();
                        }}
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        className={styles.srHeader__drawerLink}
                        to="/login"
                        onClick={closeMobileNav}
                      >
                        Login
                      </Link>
                      <Link
                        className={styles.srHeader__drawerLink}
                        to="/register"
                        onClick={closeMobileNav}
                      >
                        Register
                      </Link>
                    </>
                  )}
                </div>

                {/*<div className={styles.srHeader__drawerMeta}>*/}
                {/*  <div className={styles.srHeader__drawerPill}>*/}
                {/*    <span className={styles.srHeader__pillIcon} aria-hidden="true">*/}
                {/*      🌍*/}
                {/*    </span>*/}
                {/*    <div className={styles.srHeader__pillText}>*/}
                {/*      <div className={styles.srHeader__pillPrimary}>Bălți</div>*/}
                {/*      <div className={styles.srHeader__pillSecondary}>Moldova</div>*/}
                {/*    </div>*/}
                {/*  </div>*/}
                {/*  <div className={styles.srHeader__drawerPill}>*/}
                {/*    <FontAwesomeIcon className={styles.srHeader__pillIconFA} icon={faClock} />*/}
                {/*    <div className={styles.srHeader__pillText}>*/}
                {/*      <div className={styles.srHeader__pillPrimary}>Luni - Duminică</div>*/}
                {/*      <div className={styles.srHeader__pillSecondary}>24/24</div>*/}
                {/*    </div>*/}
                {/*  </div>*/}
                {/*</div>*/}
              </div>
            </aside>
          </div>,
          document.body
        )}
    </header>
  );
};

export default Header;